// src/pages/mission/Mission.jsx
// Refactored mission briefing page with modular components

import React from 'react';
import { THEMES } from '../themeConfig';
import { DEMO_MISSION, DEMO_SQUAD } from './Mission.constants';
import {
  useMissionData,
  useSquadLoadouts,
  useSelectedSoldier,
  useMissionNavigation,
  useMissionDebug
} from './Mission.hooks';
import { attachLoadoutsToSquad } from './Mission.utils';
import AlertBanner from './components/AlertBanner';
import MissionHeader from './components/MissionHeader';
import MissionIntelPanel from './components/MissionIntelPanel';
import SquadRosterPanel from './components/SquadRosterPanel';
import LoadoutPanel from './components/LoadoutPanel';
import DeployButton from './components/DeployButton';

const ThemedMission = ({ theme: propTheme, mission: propMission, squad: propSquad, onStartBattle, onAbort }) => {
  // Get mission data from route or props
  const {
    mission: stateMission,
    squad: stateSquad,
    campaign, // ADDED: Get campaign object
    theme: currentTheme,
    campaignId
  } = useMissionData(propMission, propSquad, propTheme);

  // Use state data or fallback to demo
  const mission = stateMission || DEMO_MISSION;
  const squad = stateSquad || DEMO_SQUAD;

  // Manage soldier selection
  const { selectedSoldier, setSelectedSoldier } = useSelectedSoldier(squad);

  // Manage squad loadouts
  const { updateLoadout, getLoadout } = useSquadLoadouts(squad);

  // Navigation handlers - UPDATED: Pass campaign object
  const { handleStartBattle, handleAbort } = useMissionNavigation(
    currentTheme,
    campaign, // ADDED: Pass full campaign object
    campaignId,
    onStartBattle,
    onAbort
  );

  // Debug logging - UPDATED: Pass campaign
  useMissionDebug(mission, squad, currentTheme, campaign);

  // Get theme configuration
  const config = THEMES[currentTheme] || THEMES.vanilla;
  const primaryColor = config.colors.primary;
  const alertColor = config.alert.color;

  // Handle deploy button click
  const onDeploy = () => {
    const squadWithLoadouts = attachLoadoutsToSquad(squad, getLoadout);
    handleStartBattle(mission, squadWithLoadouts);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b ${config.colors.background} text-white p-8`}>
      <AlertBanner alertColor={alertColor} />

      <MissionHeader
        mission={mission}
        primaryColor={primaryColor}
        alertColor={alertColor}
        onAbort={handleAbort}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column: Mission Intel & Squad Roster */}
        <div className="lg:col-span-1 space-y-6">
          <MissionIntelPanel
            mission={mission}
            primaryColor={primaryColor}
            alertColor={alertColor}
          />

          <SquadRosterPanel
            squad={squad}
            selectedSoldier={selectedSoldier}
            onSelectSoldier={setSelectedSoldier}
            primaryColor={primaryColor}
          />
        </div>

        {/* Right Column: Equipment Loadout */}
        <div className="lg:col-span-2">
          <LoadoutPanel
            selectedSoldier={selectedSoldier}
            loadout={getLoadout(selectedSoldier?.id)}
            onUpdateLoadout={updateLoadout}
            primaryColor={primaryColor}
            alertColor={alertColor}
          />
        </div>
      </div>

      <DeployButton
        squad={squad}
        alertColor={alertColor}
        onDeploy={onDeploy}
      />
    </div>
  );
};

// Named exports for specific themes
export const VanillaMission = (props) => <ThemedMission theme="vanilla" {...props} />;
export const MajesticMission = (props) => <ThemedMission theme="majestic" {...props} />;
export const DevastatorsMission = (props) => <ThemedMission theme="devastators" {...props} />;
export const ArchboundMission = (props) => <ThemedMission theme="archbound" {...props} />;

// Default export
export default ThemedMission;